import { ArrowUpRight, Quote } from "lucide-react";

const CASES = [
  { company: "Global CPG Leader", industry: "Finance", metric: "45%", metricLabel: "faster month-end close", title: "Automating reconciliation across 12 ERPs", blurb: "AI agents matched millions of transactions and cut manual investigation dramatically." },
  { company: "Fortune 100 Bank", industry: "Financial Services", metric: "3x", metricLabel: "faster reconciliation", title: "Trusted context for finance operations", blurb: "A unified semantic layer gave every team the same reliable numbers." },
  { company: "Industrial Manufacturer", industry: "Supply Chain", metric: "20%", metricLabel: "lower inventory cost", title: "Smarter demand & inventory planning", blurb: "Balanced service levels and working capital with agentic planning." },
  { company: "National Healthcare Network", industry: "Operations", metric: "60%", metricLabel: "less manual reporting", title: "Always-current operational reporting", blurb: "Replaced static dashboards with decision-ready intelligence." },
  { company: "Global Retail Group", industry: "Marketing", metric: "2x", metricLabel: "campaign throughput", title: "Orchestrating campaigns end-to-end", blurb: "Coordinated planning, content and activation across channels." },
  { company: "Telecom Provider", industry: "Customer Service", metric: "35%", metricLabel: "faster resolution", title: "From case management to resolution", blurb: "Agents triaged, diagnosed and resolved across systems." },
  { company: "Energy Enterprise", industry: "Data & Intelligence", metric: "40+", metricLabel: "data sources unified", title: "Building an enterprise context layer", blurb: "Connected fragmented data into governed, reusable products." },
  { company: "Global Tech Firm", industry: "HR & People Ops", metric: "50%", metricLabel: "faster onboarding", title: "Reimagining the employee lifecycle", blurb: "Coordinated onboarding across HR, IT and Finance automatically." },
];

const Tag = ({ children }) => <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A5730]">{children}</span>;

/* A · 3-column metric cards */
const A = () => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
    {CASES.slice(0, 6).map((c) => (
      <div key={c.company} className="group rounded-xl bg-white border border-[#3E2818]/10 p-5 hover:border-[#8A5730]/35 hover:shadow-[0_18px_44px_-28px_rgba(62,40,24,0.4)] transition-all duration-300 flex flex-col">
        <Tag>{c.industry}</Tag>
        <div className="mt-4 flex items-baseline gap-2">
          <span className="font-display text-3xl font-medium text-[#8A5730]">{c.metric}</span>
          <span className="text-[12px] text-[#3E2818]/55">{c.metricLabel}</span>
        </div>
        <p className="text-[15px] font-semibold text-[#3E2818] mt-3 leading-snug">{c.title}</p>
        <p className="text-[12.5px] text-[#3E2818]/60 mt-2 leading-relaxed">{c.blurb}</p>
        <div className="mt-4 pt-3 border-t border-[#3E2818]/8 flex items-center justify-between">
          <span className="text-[12px] font-semibold text-[#3E2818]/70">{c.company}</span>
          <ArrowUpRight size={15} className="text-[#8A5730] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </div>
      </div>
    ))}
  </div>
);

/* B · Featured + grid */
const B = () => (
  <div className="grid lg:grid-cols-[1.1fr_1fr] gap-4">
    <div className="rounded-2xl bg-[#211307] text-[#F7F5F0] p-7 flex flex-col justify-between min-h-[300px]">
      <div>
        <Quote size={28} className="text-[#E8AA68]" />
        <p className="font-display text-xl lg:text-2xl font-medium leading-snug mt-4">&ldquo;Intugle gave our finance team a single trusted view — we close 45% faster with far less manual work.&rdquo;</p>
      </div>
      <div className="mt-6">
        <div className="flex items-baseline gap-2"><span className="font-display text-4xl font-medium text-[#E8AA68]">45%</span><span className="text-[13px] text-white/70">faster month-end close</span></div>
        <p className="text-[13px] text-white/60 mt-2">VP Finance · Global CPG Leader</p>
      </div>
    </div>
    <div className="grid sm:grid-cols-2 gap-4">
      {CASES.slice(1, 5).map((c) => (
        <div key={c.company} className="rounded-xl bg-white border border-[#3E2818]/10 p-4 flex flex-col hover:border-[#8A5730]/35 transition-colors">
          <Tag>{c.industry}</Tag>
          <span className="font-display text-2xl font-medium text-[#8A5730] mt-2">{c.metric}</span>
          <p className="text-[13px] font-semibold text-[#3E2818] mt-1 leading-snug">{c.title}</p>
          <span className="mt-auto pt-3 text-[12px] font-semibold text-[#3E2818]/60">{c.company}</span>
        </div>
      ))}
    </div>
  </div>
);

/* C · Editorial list rows */
const C = () => (
  <div className="border-t border-[#3E2818]/12">
    {CASES.slice(0, 6).map((c, i) => (
      <div key={c.company} className="group grid grid-cols-[auto_1fr_auto] items-center gap-4 lg:gap-8 py-5 border-b border-[#3E2818]/12 hover:bg-white/40 transition-colors px-2 cursor-pointer">
        <span className="font-mono2 text-[11px] text-[#8A5730]">{String(i + 1).padStart(2, "0")}</span>
        <div>
          <Tag>{c.industry} · {c.company}</Tag>
          <p className="text-[15px] lg:text-lg font-semibold text-[#3E2818] mt-1">{c.title}</p>
        </div>
        <div className="flex items-center gap-5">
          <div className="text-right hidden sm:block">
            <div className="font-display text-2xl font-medium text-[#8A5730]">{c.metric}</div>
            <div className="text-[11px] text-[#3E2818]/55">{c.metricLabel}</div>
          </div>
          <ArrowUpRight size={18} className="text-[#8A5730] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </div>
      </div>
    ))}
  </div>
);

/* D · Bento mosaic */
const D = () => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-[150px]">
    <div className="col-span-2 row-span-2 rounded-2xl bg-[#211307] text-[#F7F5F0] p-6 flex flex-col justify-between">
      <Tag>{/* */}<span className="text-[#E8AA68]">{CASES[0].industry}</span></Tag>
      <div>
        <div className="font-display text-5xl font-medium text-[#E8AA68]">{CASES[0].metric}</div>
        <p className="text-[13px] text-white/70">{CASES[0].metricLabel}</p>
        <p className="text-[15px] font-semibold mt-3">{CASES[0].title}</p>
        <p className="text-[12px] text-white/55 mt-2">{CASES[0].company}</p>
      </div>
    </div>
    {CASES.slice(1, 7).map((c, i) => (
      <div key={c.company} className={`rounded-2xl bg-white border border-[#3E2818]/10 p-5 flex flex-col justify-between hover:border-[#8A5730]/35 transition-colors ${i === 1 ? "lg:col-span-2" : ""}`}>
        <Tag>{c.industry}</Tag>
        <div>
          <span className="font-display text-2xl font-medium text-[#8A5730]">{c.metric}</span>
          <p className="text-[13px] font-semibold text-[#3E2818] mt-1 leading-snug">{c.title}</p>
        </div>
      </div>
    ))}
  </div>
);

const OPTIONS = [
  { id: "a", title: "Option A · Metric-Forward Card Grid", note: "A clean 3-column grid where each card leads with a big result metric. Scales perfectly to 8–10 stories and stays scannable. The safe, professional B2B choice.", comp: A },
  { id: "b", title: "Option B · Featured + Grid", note: "One hero case study (dark, with a quote) alongside a grid of smaller cards. Great for spotlighting your strongest story while still listing the rest.", comp: B },
  { id: "c", title: "Option C · Editorial List", note: "Full-width numbered rows with industry, title and a result metric. Very premium and dense — ideal when you have many stories and want an index-like feel.", comp: C },
  { id: "d", title: "Option D · Bento Mosaic", note: "Mixed-size tiles with one large highlight. Most visually distinctive and modern; a little more art-directed.", comp: D },
];

export default function CaseStudyOptions() {
  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#8A5730]">Success Stories · Template Options</p>
        <h1 className="font-display text-2xl lg:text-3xl font-medium mt-2 tracking-tight text-[#3E2818]">Pick a layout for the case studies section</h1>
        <p className="text-sm text-[#3E2818]/60 mt-3 max-w-2xl leading-relaxed">Rendered with placeholder stories (metrics are illustrative). Each scales to 8–10 case studies. Tell me the letter.</p>

        <div className="mt-12 space-y-16">
          {OPTIONS.map((o) => {
            const C = o.comp;
            return (
              <div key={o.id}>
                <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#B87A32] mb-4">{o.title}</p>
                <C />
                <p className="text-[12px] text-[#3E2818]/55 mt-4 leading-snug max-w-3xl">{o.note}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
